import { asciiSign, formatNumber, normaliseSign } from '@staiter/i18n/format';
import { type ChangeEvent, forwardRef, useCallback, useState } from 'react';
import { useLocale } from '~/lib/hooks/useLocale';
import { Input } from './Input';

export type Currency = 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY';

const CURRENCY_SYMBOL: Record<Currency, string> = {
  KZT: '₸',
  USD: '$',
  EUR: '€',
  RUB: '₽',
  CNY: '¥',
};

export type MoneyInputProps = {
  /** Raw Decimal string value */
  value: string;
  onChange: (next: string) => void;
  currency?: Currency;
  /**
   * D3.2 (Z-F3, Н-12) — валюта не должна заявляться дважды.
   *
   * UX раунда 7: рядом с полем стоит отдельный селект «Валюта», и суффикс в поле
   * повторяет тот же факт. Повтор не безобиден: два источника расходятся в момент
   * переключения селекта, и пользователю приходится решать, какому верить.
   * Форма с собственным селектом ставит `false`; одиночное денежное поле (счета,
   * платежи, landed cost) оставляет суффикс — там он единственный носитель валюты.
   */
  showCurrencySuffix?: boolean;
  label?: string;
  hideLabel?: boolean;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
};

/**
 * FE-72 (Sprint 9 rework) — MoneyInput primitive.
 * - Controlled with raw Decimal-string value.
 * - On blur: formatted with thousand separators (Russian locale).
 * - On focus: reverts to raw digits для editing.
 * - inputMode=decimal, tabular-nums font.
 * - Currency suffix chip (right side).
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  function MoneyInput(props, ref): JSX.Element {
    const {
      value,
      onChange,
      currency = 'KZT',
      showCurrencySuffix = true,
      label,
      hideLabel,
      ...rest
    } = props;
    const [focused, setFocused] = useState(false);
    const [draft, setDraft] = useState(value);
    const locale = useLocale();

    const displayValue = focused
      ? draft
      : (() => {
          const n = Number(value);
          if (!Number.isFinite(n)) return value;
          // D3.2 — было `toLocaleString('ru-KZ')`: в en-локали сумма оставалась
          // отформатированной по-русски независимо от переводов. Формат идёт от
          // локали пользователя через общий форматтер (класс `locale` барьера D2.2).
          // F101 (NC-45): в `value` поля — ASCII-знак (`Number('−5')` = NaN, правка терялась).
          return asciiSign(
            formatNumber(n, locale, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          );
        })();

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        // Allow digits, comma, dot, minus, space (thousand sep on paste).
        // F101 (NC-45): вставка «−1 000,00» (U+2212 из показа) принимается как «-1000.00».
        const raw = normaliseSign(e.target.value).replace(/\s/g, '').replace(',', '.');
        if (raw === '' || raw === '-' || /^-?\d*\.?\d*$/.test(raw)) {
          setDraft(raw);
          onChange(raw);
        }
      },
      [onChange],
    );

    // Суффикс валюты — в штатный слот `trailingIcon` примитива, а не абсолютным
    // позиционированием поверх поля. Абсолютный якорь был хрупким по своей
    // природе: он мерился от обёртки, куда `Input` рендерит ещё и `hint`/`error`,
    // и первое же поле с ошибкой уронило бы символ в текст ошибки.
    return (
      <Input
        ref={ref}
        label={label ?? ''}
        hideLabel={hideLabel ?? label === undefined}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={() => {
          setFocused(true);
          setDraft(value);
        }}
        onBlur={() => setFocused(false)}
        trailingIcon={
          showCurrencySuffix ? (
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'var(--font-mono)',
                // D3.2 — было число 13: кегль суффикса не следовал за шкалой и
                // не садился на базовую линию числа при смене размера поля.
                fontSize: 'var(--fs-13)',
                lineHeight: 1,
                color: 'var(--text-muted)',
              }}
            >
              {CURRENCY_SYMBOL[currency]}
            </span>
          ) : undefined
        }
        style={{
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'right',
        }}
        {...rest}
      />
    );
  },
);
